import React from "react";
import HeadNext from "next/head";
import { useRouter } from "next/router";

import {
  DEFAULT_LOCALE,
  LOCALES,
  isTranslatedRoute,
  localizePathname,
  normalizePathname,
  useLocale,
} from "lib/i18n";

interface HeadProps {
  title?: string;
  /**
   * Accepts url location in relative format
   */
  url?: string;
  /**
   * Accepts image location in relative format
   */
  imageUrl?: string;
  desc?: string;
}

/**
 * Use this component safely down in the component tree without worrying
 * about duplicate meta tags
 */
const Head: React.FC<HeadProps> = ({
  title,
  url,
  imageUrl,
  desc,
  children,
}) => {
  const hostUrl = process.env.NEXT_PUBLIC_HOMEPAGE_URL;
  const locale = useLocale();
  const router = useRouter();

  const imageUrlFull = imageUrl?.includes("http")
    ? imageUrl
    : `${hostUrl}${imageUrl}`;

  // The route this page represents, locale-stripped. `url` is the page's own
  // declaration and wins; router.asPath covers callers that pass no url.
  const routePathname = normalizePathname(
    url && !url.includes("http") ? url : router.asPath || "/",
  );
  const isTranslated = isTranslatedRoute(routePathname);

  // Canonical must point at the current locale's own URL, otherwise every
  // /es/* page would declare the English page as canonical and ask Google to
  // drop it from the index.
  const canonicalPathname = localizePathname(routePathname, locale);
  const urlFull = url?.includes("http")
    ? url
    : `${hostUrl}${canonicalPathname}`;

  // Only reciprocate hreflang for routes that genuinely exist in every locale.
  // Advertising an alternate that is still English is worse than advertising
  // none. Everything else on /es/* gets noindex so the untranslated copy does
  // not compete with the English original.
  const shouldNoindex = !isTranslated && locale !== DEFAULT_LOCALE;

  return (
    <HeadNext>
      {title && (
        <>
          <title>{title}</title>
          <meta name="twitter:title" content={title} />
          {/* we provide key for meta properties so that the Head component can dedupe them
          identical meta names are deduped automatically */}
          <meta property="og:title" content={title} key="og:title" />
        </>
      )}
      {desc && (
        <>
          <meta name="description" content={desc} />
          <meta name="twitter:description" content={desc} />
          <meta property="og:description" content={desc} key="og:description" />
        </>
      )}
      {imageUrl && (
        <>
          <meta name="image" content={imageUrlFull} />
          <meta name="twitter:image" content={imageUrlFull} />
          <meta property="og:image" content={imageUrlFull} key="og:image" />
        </>
      )}
      {url && (
        <>
          <link rel="canonical" href={urlFull} key="link:canonical" />
          <meta name="twitter:url" content={urlFull} />
          <meta property="og:url" content={urlFull} key="og:url" />
        </>
      )}
      {isTranslated && (
        <>
          {LOCALES.map((alternate) => (
            <link
              key={`alternate:${alternate}`}
              rel="alternate"
              hrefLang={alternate}
              href={`${hostUrl}${localizePathname(routePathname, alternate)}`}
            />
          ))}
          <link
            key="alternate:x-default"
            rel="alternate"
            hrefLang="x-default"
            href={`${hostUrl}${localizePathname(
              routePathname,
              DEFAULT_LOCALE,
            )}`}
          />
        </>
      )}
      {shouldNoindex && (
        <meta name="robots" content="noindex, follow" key="meta:robots" />
      )}
      {children}
    </HeadNext>
  );
};

export default Head;
